import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { Button, Card, CardBody, CardHeader, Input } from '../components/ui/index.ts';
import { useAuth } from '../hooks/useAuth.tsx';

/**
 * LoginPage — TASK-03
 *
 * Design: Tibo CRM (indigo gradient, primary-700 branding, elevated cards).
 * Auth flow: POSTs credentials to admin-api /auth/login (future) or
 * Cognito USER_SRP_AUTH (production). In this scaffold, accepts any
 * non-empty token directly (dev mode) — wired for Cognito in production.
 */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | undefined)?.from?.pathname ?? '/tenants';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
      const cognitoRegion =
        (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'us-east-1';

      let token: string;

      if (cognitoClientId) {
        // B.2 — Real Cognito: InitiateAuth with USER_PASSWORD_AUTH flow
        // Standard pattern: Cognito responds with IdToken in AuthenticationResult
        const cognitoEndpoint = `https://cognito-idp.${cognitoRegion}.amazonaws.com/`;

        const res = await fetch(cognitoEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          },
          body: JSON.stringify({
            AuthFlow: 'USER_PASSWORD_AUTH',
            ClientId: cognitoClientId,
            AuthParameters: {
              USERNAME: email,
              PASSWORD: password,
            },
          }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({ message: 'Error de autenticación' }));
          throw new Error((errBody as { message?: string }).message ?? 'Credenciales inválidas');
        }

        const body = (await res.json()) as {
          AuthenticationResult?: { IdToken?: string };
          ChallengeName?: string;
          Session?: string;
        };

        if (body.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          navigate('/auth/change-password', {
            state: { session: body.Session, email },
            replace: true,
          });
          return;
        }
        if (body.ChallengeName) {
          throw new Error(`Cognito challenge no soportado: ${body.ChallengeName}`);
        }

        if (!body.AuthenticationResult?.IdToken) {
          throw new Error('Respuesta de Cognito sin IdToken');
        }

        token = body.AuthenticationResult.IdToken;
      } else {
        // Dev fallback: call admin-api /auth/login (for local testing without Cognito)
        const res = await fetch(
          `${(import.meta.env.VITE_API_URL as string | undefined) ?? '/api'}/auth/login`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          },
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: 'Credenciales inválidas' }));
          throw new Error((body as { error: string }).error ?? 'Credenciales inválidas');
        }

        const body = (await res.json()) as { token: string };
        token = body.token;
      }

      login(token);
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError((err as Error).message ?? 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-primary-50 p-4">
      <Card variant="elevated" className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* El logotipo reemplaza al texto: el alt lleva el nombre para que
              siga siendo un h1 legible por lectores de pantalla. */}
          <h1 className="flex justify-center">
            <img src="/tibo-logo.svg" alt="Tibo CRM" className="h-10 w-auto" />
          </h1>
          <p className="text-body-sm text-content-tertiary mt-2">Panel de Administración</p>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && <ErrorAlert message={error} />}
            <Input
              id="login-email"
              label="Email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@clinica.com"
            />
            <Input
              id="login-password"
              label="Contraseña"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
            />
            <Button
              id="login-submit"
              variant="primary"
              size="lg"
              className="w-full mt-2"
              loading={loading}
              type="submit"
            >
              Iniciar sesión
            </Button>
          </form>
          <p className="text-center text-xs text-content-tertiary mt-8 font-mono">
            Solo usuarios autorizados por super_admin
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
